import { Camera, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { ImageCropper } from '@/components/shared/image-cropper';
import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/utils/cn';

export const DefaultImageTile = ({
  label,
  src,
  caption,
  canRemove,
  disabled,
  onUpload,
  onRemove,
  labels,
}: {
  label: string;
  src: string | null;
  caption?: string;
  canRemove: boolean;
  disabled?: boolean;
  onUpload: (blob: Blob) => Promise<void>;
  onRemove?: () => void;
  labels: {
    change: string;
    remove: string;
    cropTitle: string;
    zoom: string;
    apply: string;
    cancel: string;
    instructions: string;
    closeCropper: string;
  };
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [candidate, setCandidate] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(
    () => () => {
      if (candidate) URL.revokeObjectURL(candidate);
    },
    [candidate],
  );

  return (
    <FormField label={label} hint={caption}>
      {() => (
        <div className="relative">
          <button
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            aria-label={labels.change}
            className={cn(
              'group relative aspect-square w-full overflow-hidden rounded-lg border bg-surface-sunken transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
              src ? 'border-border' : 'border-dashed border-border-strong',
              disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-brand/50',
            )}
          >
            {src ? (
              <img src={src} alt="" className="size-full object-cover" />
            ) : (
              <span className="flex size-full items-center justify-center text-fg-tertiary">
                <Camera className="size-6" aria-hidden />
              </span>
            )}
            <span
              aria-hidden
              className={cn(
                'absolute inset-0 flex items-center justify-center bg-overlay/55 text-fg-inverse opacity-0 transition-opacity',
                'group-hover:opacity-100 group-focus-visible:opacity-100',
              )}
            >
              <Camera className="size-5" />
            </span>
          </button>
          {canRemove && onRemove ? (
            <Button
              type="button"
              variant="secondary"
              size="icon"
              disabled={disabled}
              aria-label={labels.remove}
              className="absolute top-1.5 right-1.5 size-7 shadow-e1"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onRemove();
              }}
            >
              <X className="size-3.5" aria-hidden />
            </Button>
          ) : null}
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) setCandidate(URL.createObjectURL(file));
              event.target.value = '';
            }}
          />
          <Dialog open={candidate !== null} onOpenChange={(open) => !open && setCandidate(null)}>
            <DialogContent size="md" closeLabel={labels.closeCropper}>
              <DialogHeader>
                <DialogTitle>{labels.cropTitle}</DialogTitle>
              </DialogHeader>
              {candidate ? (
                <ImageCropper
                  src={candidate}
                  aspect={1}
                  circular={false}
                  maxSize={512}
                  outputType="image/png"
                  applying={uploading}
                  onCancel={() => setCandidate(null)}
                  onCropped={async (blob) => {
                    setUploading(true);
                    try {
                      await onUpload(blob);
                      setCandidate(null);
                    } finally {
                      setUploading(false);
                    }
                  }}
                  labels={{
                    zoom: labels.zoom,
                    apply: labels.apply,
                    cancel: labels.cancel,
                    instructions: labels.instructions,
                  }}
                />
              ) : null}
            </DialogContent>
          </Dialog>
        </div>
      )}
    </FormField>
  );
};
